import { Button, Checkbox, Flex, Modal, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { showErrorNotifications } from "../../utils/handlers/handle-error"
import { syncContractToLead, syncContractToProposal } from "./api/services.ts"
import { ComparisonTable, type ComparisonTableColumn } from "./ComparisonTable.tsx"
import type { ContractDiffRow } from "./contract-generate-diff.ts"
import { mapFieldComparisonToGroupedValues } from "./grouped-comparison-adapter.ts"

const { Text, Title } = Typography

interface ContractGenerateConfirmModalProps {
  open: boolean
  /** Cancel — abandons Generate entirely: no save-file call, no navigate. */
  onClose: () => void
  /**
   * Pre-computed rows (see `buildContractGenerateDiffRows`) — already
   * compared against the relevant baseline (linked Proposal if one exists,
   * else Lead) and already excludes `sales_owner`. Filtered again here,
   * defensively, so it can never appear even if a caller forgets to strip it.
   */
  rows: ContractDiffRow[]
  contractId: string
  hasLinkedProposal: boolean
  /** `document_label` from the same `getContractSyncStatus` call that
   * produced `rows` — confirmed live as a top-level field alongside
   * `fields`/`diff_count`, e.g. "Contract · v2". Falls back to the bare
   * "Contract" when absent. Labels only the Contract column — the
   * baseline column (Lead or, when linked, Proposal) has no equivalent
   * label available from this same response (confirmed live: Contract's
   * own sync-status carries no embedded Proposal label), so it keeps its
   * existing static "Lead"/"Proposal" text. */
  documentLabel?: string
  /**
   * Finishes the Generate action (save-file + navigate) — called after any
   * checked syncs complete, including when neither box is checked
   * ("Continue" case — the Contract's own edits still get generated).
   */
  onProceed: () => void | Promise<void>
}

/**
 * Shown only when Generate would otherwise leave the Lead (and, if linked,
 * the Proposal) out of sync with edits just made on this Contract. Two
 * independent, unchecked-by-default choices — "Update Lead" and "Update
 * Proposal" (the latter only offered when a Proposal is actually linked).
 * The Contract's own edits are never touched by anything chosen here, Cancel
 * included — they were already saved by the PATCH that runs before this
 * modal opens.
 */
function ContractGenerateConfirmModal({
  open,
  onClose,
  rows,
  contractId,
  hasLinkedProposal,
  documentLabel,
  onProceed
}: ContractGenerateConfirmModalProps) {
  const { t } = useTranslation()
  const [updateLead, setUpdateLead] = useState(false)
  const [updateProposal, setUpdateProposal] = useState(false)
  const [saving, setSaving] = useState(false)

  const visibleRows = rows.filter((row) => row.key !== "sales_owner")

  const baselineColumnKey = hasLinkedProposal ? "proposal" : "lead"
  const columns: ComparisonTableColumn[] = [
    {
      key: baselineColumnKey,
      label: hasLinkedProposal
        ? t("documentSync.contractGenerateConfirm.proposalColumn")
        : t("documentSync.contractGenerateConfirm.leadColumn")
    },
    {
      key: "contract",
      label:
        documentLabel || t("documentSync.contractGenerateConfirm.contractColumn")
    }
  ]

  useEffect(() => {
    if (!open) return
    setUpdateLead(false)
    setUpdateProposal(false)
  }, [open])

  // Independent writes to different targets — sequential awaits here used to
  // roughly double the wait before the modal could proceed when both boxes
  // were checked. `allSettled` (not `all`) so a Lead failure never hides a
  // concurrent Proposal failure behind a fail-fast rejection — both outcomes
  // are checked and named explicitly in the thrown message.
  const handleConfirm = async () => {
    try {
      setSaving(true)

      const proposalFields = visibleRows
        .filter((row) => row.syncableToDocument)
        .map((row) => row.key)
      const shouldUpdateProposal =
        updateProposal && hasLinkedProposal && proposalFields.length > 0

      const [leadResult, proposalResult] = await Promise.allSettled([
        updateLead
          ? syncContractToLead(
              contractId,
              visibleRows.map((row) => row.key)
            )
          : Promise.resolve(null),
        shouldUpdateProposal
          ? syncContractToProposal(contractId, proposalFields)
          : Promise.resolve(null)
      ])

      const leadFailed =
        updateLead &&
        (leadResult.status === "rejected" ||
          (leadResult.status === "fulfilled" && !leadResult.value?.ok))
      const proposalFailed =
        shouldUpdateProposal &&
        (proposalResult.status === "rejected" ||
          (proposalResult.status === "fulfilled" && !proposalResult.value?.ok))

      if (leadFailed && proposalFailed) {
        throw new Error(t("documentSync.contractGenerateConfirm.updateBothFailed"))
      }
      if (leadFailed) {
        throw new Error(t("documentSync.contractGenerateConfirm.updateLeadFailed"))
      }
      if (proposalFailed) {
        throw new Error(t("documentSync.contractGenerateConfirm.updateProposalFailed"))
      }

      onClose()
      await onProceed()
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }

  const confirmLabel =
    updateLead || updateProposal
      ? t("documentSync.contractGenerateConfirm.updateSelected")
      : t("documentSync.contractGenerateConfirm.continue")

  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onClose}
      maskClosable={false}
      centered
      width={680}
      // A Scope/Payment Terms row with many milestones/payments must never
      // grow the modal past a sane height (or push Cancel/Update off the
      // visible viewport) — only this body scrolls; the header and footer
      // (rendered as siblings of `.ant-modal-body`, not inside it) stay
      // pinned outside the scroll area for free.
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {t("documentSync.contractGenerateConfirm.title")}
        </Title>
      }
      footer={[
        <Button key={"cancel"} disabled={saving} onClick={onClose}>
          {t("documentSync.contractGenerateConfirm.cancel")}
        </Button>,
        <Button
          key={"confirm"}
          type={"primary"}
          loading={saving}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
      ]}
    >
      <Text>
        {t("documentSync.contractGenerateConfirm.itemsChanged", {
          count: visibleRows.length
        })}
      </Text>

      <div className={"my-4"} data-testid={"contract-generate-confirm-fields"}>
        <ComparisonTable
          columns={columns}
          testIdPrefix={"contract-generate-confirm"}
          rows={visibleRows.map((row) => {
            // `contract` is always the "document being generated" side here
            // — the baseline (Lead or, when linked, Proposal) is whatever
            // `scope_diff`/`payment_terms_diff` were computed against by
            // this same `sync-status` call, regardless of which one it is.
            const grouped = mapFieldComparisonToGroupedValues(
              row.sourceField,
              [row.baselineSource, "contract"],
              t,
              "contract"
            )

            return {
              key: row.key,
              label: row.label,
              hasDiff: true,
              note: !row.syncableToDocument
                ? t("documentSync.contractGenerateConfirm.leadOnlyNote")
                : undefined,
              values: {
                [row.baselineSource]: row.baselineDisplay,
                contract: row.contractDisplay
              },
              groupedValues: grouped?.groupedValues,
              groupedCellCopy: grouped?.groupedCellCopy
            }
          })}
        />
      </div>

      <Text type={"secondary"} className={"block mb-3"}>
        {t("documentSync.contractGenerateConfirm.whereToUpdate")}
      </Text>

      <Flex vertical gap={8}>
        <Checkbox
          checked={updateLead}
          onChange={(event) => setUpdateLead(event.target.checked)}
        >
          {t("documentSync.contractGenerateConfirm.updateLead")}
        </Checkbox>
        {hasLinkedProposal && (
          <Checkbox
            checked={updateProposal}
            onChange={(event) => setUpdateProposal(event.target.checked)}
          >
            {t("documentSync.contractGenerateConfirm.updateProposal")}
          </Checkbox>
        )}
      </Flex>
    </Modal>
  )
}

export { ContractGenerateConfirmModal }
